import { Component, OnDestroy, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MarriottPmsMessagesWidget, ProjectCMSWidgetType, LogService, SpecificActionSubType } from 'htv-lib'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'
import { EventService } from '../../services/event.service'
import { StateService } from '../../services/state.service'
import { MessageService } from '../../services/message.service'
import { MessageStateService } from '../../services/message-state.service'
import { MessageDto } from '../../interfaces/message'
import { Subscription } from 'rxjs'
import { TranslateModule } from '@ngx-translate/core'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import { MomentPipe } from '../../pipes/moment.pipe'
import { WidgetActionService } from '../../services/widget-action.service'

@Component({
  selector: 'app-marriott-pms-messages',
  standalone: true,
  imports: [CommonModule, TranslateModule, MomentPipe],
  templateUrl: './marriott-pms-messages.component.html',
  styleUrls: ['./marriott-pms-messages.component.scss'],
})
export class MarriottPmsMessagesComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  currentPage: 1 | 2 = 1
  messages: MessageDto[] = []
  selectedMessage?: MessageDto
  unreadCount = 0
  informativeText = 'WIDGETS.MARRIOTT_PMS_MESSAGES.NO_NEW_MESSAGES'

  private eventSubscription?: Subscription

  constructor(
    private eventService: EventService,
    private stateService: StateService,
    private messageService: MessageService,
    private messageStateService: MessageStateService,
    private appButtonController: HTVButtonControllerDirective,
    private logService: LogService,
    private widgetActionService: WidgetActionService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.loadMessages()
    this.messageStateService.refreshMessages()
    this.stateService.startShowingWidget(ProjectCMSWidgetType.MARRIOTT_PMS_MESSAGES)
    this.registerEventListener()
    this.registerWidgetActionListener()
  }

  registerWidgetActionListener(): void {
    this.subscriptions.add(
      this.widgetActionService.messageWidgetAction$.subscribe(action => {
        this.handleWidgetAction(action)
      })
    )
  }

  handleWidgetAction(action: SpecificActionSubType): void {
    switch (action) {
      case SpecificActionSubType.DELETE_MESSAGE:
        this.deleteMessage()
        break
    }
  }

  override ngOnDestroy(): void {
    this.stateService.stopShowingWidget(ProjectCMSWidgetType.MARRIOTT_PMS_MESSAGES)
    this.eventSubscription?.unsubscribe()
    super.ngOnDestroy()
  }

  registerEventListener(): void {
    this.eventSubscription = this.eventService.keyDown.subscribe(key => {
      this.handleKeyPress(key)
    })
  }

  handleKeyPress(key: string): void {
    if (key === 'BACK' || key === 'Escape') {
      if (this.currentPage === 2) {
        this.goBack()
      }
    }
  }

  loadMessages(): void {
    this.subscriptions.add(
      this.messageStateService.messages$.subscribe({
        next: messages => {
          this.messages = messages
          this.unreadCount = messages.filter(m => !m.readAt).length
          this.updateInformativeText()
          this.appButtonController.forceRefreshSelectedButton()
          this.logService.debug(
            `[MarriottPmsMessagesComponent] Loaded ${messages.length} messages, ${this.unreadCount} unread`
          )

          messages
            .filter(m => !m.deliveredAt)
            .forEach(message => {
              this.subscriptions.add(
                this.messageService.markAsDelivered(message.id).subscribe({
                  next: () => {
                    this.logService.debug(`[MarriottPmsMessagesComponent] Message ${message.id} marked as delivered`)
                    this.messageStateService.refreshMessages()
                  },
                  error: err => {
                    this.logService.error(`[MarriottPmsMessagesComponent] Failed to mark message as delivered: ${err}`)
                  },
                })
              )
            })
        },
        error: err => {
          this.logService.error(`[MarriottPmsMessagesComponent] Failed to load messages: ${err}`)
        },
      })
    )
  }

  selectMessage(message: MessageDto): void {
    this.selectedMessage = message
    this.currentPage = 2
    this.appButtonController.forceRefreshSelectedButton()

    if (!message.readAt) {
      this.subscriptions.add(
        this.messageService.markAsRead(message.id).subscribe({
          next: updatedMessage => {
            this.logService.debug(`[MarriottPmsMessagesComponent] Message ${message.id} marked as read`)
            this.selectedMessage = updatedMessage
            this.messageStateService.refreshMessages()
          },
          error: err => {
            this.logService.error(`[MarriottPmsMessagesComponent] Failed to mark message as read: ${err}`)
          },
        })
      )
    }
  }

  goBack(): void {
    this.currentPage = 1
    this.selectedMessage = undefined
    this.appButtonController.forceRefreshSelectedButton()
  }

  deleteMessage(): void {
    if (!this.selectedMessage) return

    const messageId = this.selectedMessage.id
    this.subscriptions.add(
      this.messageService.deleteMessage(messageId).subscribe({
        next: () => {
          this.logService.debug(`[MarriottPmsMessagesComponent] Message ${messageId} deleted`)
          this.goBack()
          this.messageStateService.refreshMessages()
        },
        error: err => {
          this.logService.error(`[MarriottPmsMessagesComponent] Failed to delete message: ${err}`)
        },
      })
    )
  }

  updateInformativeText(): void {
    if (this.unreadCount > 0) {
      this.informativeText =
        this.unreadCount === 1
          ? 'WIDGETS.MARRIOTT_PMS_MESSAGES.ONE_NEW_MESSAGE'
          : 'WIDGETS.MARRIOTT_PMS_MESSAGES.N_NEW_MESSAGES'
    } else {
      this.informativeText = 'WIDGETS.MARRIOTT_PMS_MESSAGES.NO_NEW_MESSAGES'
    }
  }

  override applyStyles(): void {
    super.applyStyles()

    const config = this.config as MarriottPmsMessagesWidget
    const widgetStyle: Record<string, string | number> = {}
    const headerStyle: Record<string, string | number> = {}
    const messageListStyle: Record<string, string | number> = {}
    const messageRowSelectedStyle: Record<string, string | number> = {}
    const unreadIndicatorStyle: Record<string, string | number> = {}
    const detailHeaderSectionStyle: Record<string, string | number> = {}
    const detailContentSectionStyle: Record<string, string | number> = {}
    const detailActionsSectionStyle: Record<string, string | number> = {}
    const actionButtonStyle: Record<string, string | number> = {}
    const actionButtonSelectedStyle: Record<string, string | number> = {}
    const infoSectionStyle: Record<string, string | number> = {}

    if (config.backgroundColor) {
      headerStyle['background-color'] = config.backgroundColor
      messageListStyle['background-color'] = config.backgroundColor
      detailHeaderSectionStyle['background-color'] = config.backgroundColor
      detailContentSectionStyle['background-color'] = config.backgroundColor
      detailActionsSectionStyle['background-color'] = config.backgroundColor
      infoSectionStyle['background-color'] = config.backgroundColor
    }

    if (config.textColor) {
      widgetStyle['color'] = config.textColor
      messageRowSelectedStyle['color'] = config.textColor
      unreadIndicatorStyle['background-color'] = config.textColor
      actionButtonStyle['color'] = config.textColor
      actionButtonStyle['border-color'] = config.textColor
      actionButtonSelectedStyle['color'] = config.textColor
    }

    if (config.buttonColor) {
      messageRowSelectedStyle['border-color'] = config.buttonColor
      actionButtonSelectedStyle['border-color'] = config.buttonColor
    }

    this.setRule('.pms-messages-widget', widgetStyle)
    this.setRule('.header', headerStyle)
    this.setRule('.message-list', messageListStyle)
    this.setRule('.message-row.htv-selected', messageRowSelectedStyle)
    this.setRule('.unread-indicator', unreadIndicatorStyle)
    this.setRule('.detail-header-section', detailHeaderSectionStyle)
    this.setRule('.detail-content-section', detailContentSectionStyle)
    this.setRule('.detail-actions-section', detailActionsSectionStyle)
    this.setRule('.action-button', actionButtonStyle)
    this.setRule('.action-button.htv-selected', actionButtonSelectedStyle)
    this.setRule('.info-section', infoSectionStyle)
  }
}
